import type { LayerEntry, LayerFlag } from './types';

const VALID_ID = /^[a-zA-Z0-9_-]+$/;
const VALID_FLAGS: ReadonlySet<LayerFlag> = new Set(['default', 'secondary']);

export function parseLayersTxt(text: string): LayerEntry[] {
  const entries: LayerEntry[] = [];
  const seen = new Set<string>();
  const lines = text.split(/\r?\n/);

  lines.forEach((rawLine, idx) => {
    const lineNo = idx + 1;
    const stripped = rawLine.replace(/\s+#.*$/, '').trim();
    if (!stripped || stripped.startsWith('#')) return;

    const tokens = stripped.split(/\s+/);
    if (tokens.length < 2) {
      throw new Error(`layers.txt:${lineNo}: expected "<id> <url> [flags]"`);
    }

    const [id, url, flagsToken] = tokens;
    if (!VALID_ID.test(id)) {
      throw new Error(`layers.txt:${lineNo}: invalid id "${id}" (allowed: A-Z a-z 0-9 _ -)`);
    }
    if (seen.has(id)) {
      throw new Error(`layers.txt:${lineNo}: duplicate id "${id}"`);
    }
    if (!/^https?:\/\//.test(url)) {
      throw new Error(`layers.txt:${lineNo}: url must be absolute http(s)`);
    }

    const flags = new Set<LayerFlag>();
    let min_scale: number | undefined;
    if (flagsToken) {
      for (const raw of flagsToken.split(',')) {
        const f = raw.trim();
        if (!f) continue;
        if (f.includes('=')) {
          const [key, value] = f.split('=', 2).map((s) => s.trim());
          if (key === 'min_scale') {
            const parsed = Number(value);
            if (!Number.isFinite(parsed) || parsed <= 0) {
              throw new Error(`layers.txt:${lineNo}: min_scale must be a positive number`);
            }
            min_scale = parsed;
            continue;
          }
          throw new Error(`layers.txt:${lineNo}: unknown option "${key}"`);
        }
        if (!VALID_FLAGS.has(f as LayerFlag)) {
          throw new Error(`layers.txt:${lineNo}: unknown flag "${f}"`);
        }
        flags.add(f as LayerFlag);
      }
    }

    seen.add(id);
    entries.push({ id, url, flags, min_scale });
  });

  return entries;
}
